// Estratégia anti-ruptura do Miguel: a partir das projeções de ruptura (atual /
// com o plano dentro do teto / com o plano + além do teto) decide se a meta de
// ≤5% é alcançada, se é preciso comprar acima do teto, e qual o resíduo que
// comprar NÃO resolve (itens sem fornecedor ou sem sugestão). Puro/determinístico.

import { isRupturaAcionavel } from './replenishment'
import { RUPTURA_TARGET, type RupturaForecast } from './rupturaForecast'
import type { KanbanStatusOp, PurchaseOrderWithItems, Supplier } from '../types'
import type { PlanoGrupo } from './planoDeCompra'

const fmtPct = (frac: number) => `${(frac * 100).toFixed(0)}%`

/** Item em ruptura visto pela estratégia (o que comprar resolve ou não). */
export interface StrategyRupturaRow {
  current_stock: number
  kanban_status: KanbanStatusOp
  has_supplier: boolean
  /** Lead time do fornecedor (null = sem fornecedor/lead → não reabastecível). */
  lead_time: number | null
}

export interface RupturaStrategyInput {
  /** Projeção só com os pedidos a caminho. */
  baseline: RupturaForecast
  /** Projeção com o plano que cabe no teto (fits). */
  withFits: RupturaForecast
  /** Projeção com o plano + itens além do teto (fits + overflows). */
  withFitsAndOverflows: RupturaForecast
  /** Itens hoje em ruptura (para o resíduo não-comprável). */
  rows: ReadonlyArray<StrategyRupturaRow>
  fitsCount: number
  overflowCount: number
  overflowValue: number
  target?: number
}

export interface RupturaStrategy {
  target: number
  currentIndex: number
  fitsEndIndex: number
  overflowsEndIndex: number
  /** O plano dentro do teto fecha o horizonte na meta. */
  atingeComFits: boolean
  /** O plano + além do teto fecha o horizonte na meta. */
  atingeComOverflows: boolean
  /** Fits não bastam, mas fits + overflows sim → vale comprar acima do teto. */
  precisaAlemDoTeto: boolean
  alemDoTetoCount: number
  alemDoTetoValue: number
  /** Itens em ruptura NÃO reabastecíveis (comprar não resolve): formam o piso. */
  semFornecedorCount: number
  /** Em ruptura com fornecedor mas sem lead time cadastrado. */
  semLeadCount: number
  naoReabastecivelCount: number
  /** Frase-resumo da estratégia. */
  headline: string
}

/**
 * Converte grupos do plano de compra em "pedidos planejados" (Pendente) para
 * alimentar a projeção: cada grupo vira um pedido com o lead time do fornecedor
 * (chegada = hoje + lead). Grupos sem fornecedor/lead ficam sem data e não
 * reduzem a projeção (não há como receber). qty = total_suggestion do item.
 */
export function plannedOrdersFromGroups(groups: ReadonlyArray<PlanoGrupo>): PurchaseOrderWithItems[] {
  return groups.map((g, gi) => {
    const suppliers = g.lead_time_days != null
      ? ({ id: g.fornecedor_id ?? `plan-${gi}`, name: g.fornecedor_nome, lead_time_days: g.lead_time_days } as Supplier)
      : null
    return {
      id: `planned-${gi}`,
      company_id: 'plan',
      supplier_id: g.fornecedor_id,
      order_number: `PLAN-${gi}`,
      status: 'Pendente',
      total_value: 0,
      notes: null,
      pdf_path: null,
      created_at: new Date().toISOString(),
      sent_at: null,
      received_at: null,
      estimated_arrival: null,
      order_type: 'compra',
      suppliers,
      purchase_order_items: g.rows.map((r, ri) => ({
        id: `planned-${gi}-${ri}`,
        company_id: 'plan',
        purchase_order_id: `planned-${gi}`,
        product_id: r.id,
        sku: r.sku,
        product_name: r.name,
        quantity: r.total_suggestion,
        unit_cost: r.cost,
        subtotal: r.total_suggestion * r.cost,
        stock_at_emission: null,
      })),
    }
  })
}

export function buildRupturaStrategy(input: RupturaStrategyInput): RupturaStrategy {
  const target = input.target ?? RUPTURA_TARGET
  const { baseline, withFits, withFitsAndOverflows, rows } = input

  const rupturaRows = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status))
  const semFornecedorCount = rupturaRows.filter(r => !r.has_supplier).length
  const semLeadCount = rupturaRows.filter(r => r.has_supplier && r.lead_time == null).length
  // Não reabastecível = sem fornecedor OU sem lead time → comprar/recompra não resolve.
  const naoReabastecivelCount = rupturaRows.filter(r => !r.has_supplier || r.lead_time == null).length

  const atingeComFits = withFits.endIndex <= target
  const atingeComOverflows = withFitsAndOverflows.endIndex <= target
  const precisaAlemDoTeto = !atingeComFits && atingeComOverflows

  const metaPct = fmtPct(target)
  // withFits aqui já é a projeção COM reposição contínua → endIndex = piso de regime.
  let headline: string
  if (baseline.currentIndex <= target) {
    headline = `Ruptura já dentro da meta de ${metaPct}.`
  } else if (atingeComFits) {
    headline = `Aprovando o plano (${input.fitsCount} ${input.fitsCount === 1 ? 'item' : 'itens'}) e seguindo a reposição contínua, a ruptura cai de ${fmtPct(baseline.currentIndex)} e estabiliza dentro da meta de ${metaPct}.`
  } else if (precisaAlemDoTeto) {
    headline = `Dentro do teto a ruptura estabiliza em ${fmtPct(withFits.endIndex)} (acima de ${metaPct}). Para chegar à meta é preciso comprar ${input.overflowCount} ${input.overflowCount === 1 ? 'item' : 'itens'} acima do teto.`
  } else {
    const partes: string[] = []
    if (semFornecedorCount > 0) partes.push(`${semFornecedorCount} sem fornecedor`)
    if (semLeadCount > 0) partes.push(`${semLeadCount} sem lead time`)
    const cauda = partes.length ? ` ${naoReabastecivelCount} item(ns) não reabastecível(is) (${partes.join(' · ')}).` : ''
    headline = `Com o plano + reposição contínua, a ruptura estabiliza em ${fmtPct(withFitsAndOverflows.endIndex)} — acima de ${metaPct}.${cauda}`
  }

  return {
    target,
    currentIndex: baseline.currentIndex,
    fitsEndIndex: withFits.endIndex,
    overflowsEndIndex: withFitsAndOverflows.endIndex,
    atingeComFits,
    atingeComOverflows,
    precisaAlemDoTeto,
    alemDoTetoCount: input.overflowCount,
    alemDoTetoValue: input.overflowValue,
    semFornecedorCount,
    semLeadCount,
    naoReabastecivelCount,
    headline,
  }
}
